import { usePortalConfig } from '@shared/config';
import { Card, CardContent } from '@shared/components/ui/card';
import { Shield, Mail, Database, Lock, UserCheck, Cookie, FileEdit, Phone } from 'lucide-react';

export default function PrivacyPage() {
  const { branding } = usePortalConfig();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-60 pointer-events-none" />
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative container mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground mb-4">
            Privacybeleid
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {branding.name} hecht veel waarde aan de bescherming van uw persoonsgegevens.
            In dit privacybeleid leggen wij uit welke gegevens wij verzamelen en hoe wij hiermee omgaan.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">

            {/* Verantwoordelijke */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">1. Verantwoordelijke</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      De verantwoordelijke voor de verwerking van persoonsgegevens is {branding.name},
                      bereikbaar via{' '}
                      <a
                        href={`mailto:${branding.contactEmail}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {branding.contactEmail}
                      </a>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Welke gegevens */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Database className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">2. Welke gegevens verzamelen wij</h2>
                    <p className="text-muted-foreground mb-4">Wij verzamelen de volgende persoonsgegevens:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Accountgegevens:</span> E-mailadres en wachtwoord (versleuteld opgeslagen)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Gebruiksgegevens:</span> Zoekgeschiedenis en bekeken jurisprudentie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Technische gegevens:</span> IP-adres, browsertype en apparaatinformatie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Communicatiegegevens:</span> Correspondentie via ons contactformulier</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Doeleinden */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileEdit className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">3. Doeleinden van verwerking</h2>
                    <p className="text-muted-foreground mb-4">Wij verwerken uw persoonsgegevens voor de volgende doeleinden:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Het aanmaken en beheren van uw account</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Het leveren van onze diensten (toegang tot jurisprudentie database)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Het versturen van nieuwsbrieven en updates (met uw toestemming)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Het verbeteren van onze dienstverlening</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Het afhandelen van uw vragen en verzoeken</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Het voldoen aan wettelijke verplichtingen</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rechtsgronden */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">4. Rechtsgronden</h2>
                    <p className="text-muted-foreground mb-4">Wij verwerken uw persoonsgegevens op basis van de volgende rechtsgronden:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Uitvoering van een overeenkomst:</span> Voor het leveren van onze diensten</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Toestemming:</span> Voor het versturen van nieuwsbrieven</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Gerechtvaardigd belang:</span> Voor het verbeteren van onze dienstverlening en beveiliging</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Wettelijke verplichting:</span> Voor het voldoen aan fiscale en administratieve verplichtingen</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bewaartermijnen */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Database className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">5. Bewaartermijnen</h2>
                    <p className="text-muted-foreground mb-4">
                      Wij bewaren uw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden
                      waarvoor zij zijn verzameld. De volgende bewaartermijnen zijn van toepassing:
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="bg-muted/50 rounded-lg p-4 border border-border/50">
                        <p className="font-semibold text-foreground text-sm">Accountgegevens</p>
                        <p className="text-muted-foreground text-sm">Tot 1 jaar na beëindiging account</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border/50">
                        <p className="font-semibold text-foreground text-sm">Zoekgeschiedenis</p>
                        <p className="text-muted-foreground text-sm">Maximaal 12 maanden</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border/50">
                        <p className="font-semibold text-foreground text-sm">Communicatiegegevens</p>
                        <p className="text-muted-foreground text-sm">Maximaal 2 jaar</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border/50">
                        <p className="font-semibold text-foreground text-sm">Technische logs</p>
                        <p className="text-muted-foreground text-sm">Maximaal 90 dagen</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delen met derden */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserCheck className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">6. Delen met derden</h2>
                    <p className="text-muted-foreground mb-4">
                      Wij delen uw persoonsgegevens alleen met derden indien dit noodzakelijk is voor
                      het uitvoeren van onze diensten of om te voldoen aan een wettelijke verplichting.
                    </p>
                    <p className="text-muted-foreground mb-4">Wij maken gebruik van de volgende dienstverleners:</p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Supabase:</span> Database hosting en authenticatie (EU servers)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Vercel:</span> Website hosting</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Sentry:</span> Foutrapportage en monitoring</span>
                      </li>
                    </ul>
                    <p className="text-muted-foreground">
                      Met alle verwerkers hebben wij verwerkersovereenkomsten gesloten om de beveiliging
                      van uw gegevens te waarborgen.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Beveiliging */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lock className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">7. Beveiliging</h2>
                    <p className="text-muted-foreground mb-4">
                      Wij nemen passende technische en organisatorische maatregelen om uw persoonsgegevens
                      te beschermen tegen verlies, misbruik en ongeautoriseerde toegang. Dit omvat onder andere:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Versleuteling van wachtwoorden</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">HTTPS-verbindingen voor alle communicatie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Toegangscontrole op database niveau (Row Level Security)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">Regelmatige beveiligingsupdates</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Uw rechten */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserCheck className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">8. Uw rechten</h2>
                    <p className="text-muted-foreground mb-4">Op grond van de AVG heeft u de volgende rechten:</p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Recht op inzage:</span> U kunt opvragen welke gegevens wij van u verwerken</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Recht op rectificatie:</span> U kunt onjuiste gegevens laten corrigeren</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Recht op verwijdering:</span> U kunt verzoeken uw gegevens te wissen</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Recht op beperking:</span> U kunt verzoeken de verwerking te beperken</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Recht op dataportabiliteit:</span> U kunt uw gegevens in een gangbaar formaat opvragen</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Recht op bezwaar:</span> U kunt bezwaar maken tegen verwerkingen op basis van gerechtvaardigd belang</span>
                      </li>
                    </ul>
                    <p className="text-muted-foreground">
                      Om uw rechten uit te oefenen kunt u contact met ons opnemen via{' '}
                      <a
                        href={`mailto:${branding.contactEmail}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {branding.contactEmail}
                      </a>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Cookie className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">9. Cookies</h2>
                    <p className="text-muted-foreground mb-4">
                      Wij maken gebruik van cookies om onze website te laten functioneren en uw
                      voorkeuren te onthouden (zoals thema-instellingen). Wij gebruiken geen
                      tracking cookies voor advertentiedoeleinden.
                    </p>
                    <p className="text-muted-foreground mb-4">De volgende cookies worden gebruikt:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Functionele cookies:</span> Voor het onthouden van uw thema-voorkeur en sessie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground"><span className="font-semibold text-foreground">Analytische cookies:</span> Voor het verbeteren van onze dienstverlening (geanonimiseerd)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Wijzigingen */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileEdit className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">10. Wijzigingen</h2>
                    <p className="text-muted-foreground">
                      Wij kunnen dit privacybeleid van tijd tot tijd wijzigen. Wijzigingen worden
                      op deze pagina gepubliceerd. Bij belangrijke wijzigingen informeren wij u
                      per e-mail.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="border-border/50 shadow-sm">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-foreground mb-3">11. Contact en klachten</h2>
                    <p className="text-muted-foreground mb-4">
                      Heeft u vragen over dit privacybeleid of wilt u een klacht indienen?
                      Neem dan contact met ons op via{' '}
                      <a
                        href={`mailto:${branding.contactEmail}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {branding.contactEmail}
                      </a>.
                    </p>
                    <p className="text-muted-foreground">
                      U heeft ook het recht om een klacht in te dienen bij de Autoriteit
                      Persoonsgegevens ({' '}
                      <a
                        href="https://autoriteitpersoonsgegevens.nl"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-medium"
                      >
                        www.autoriteitpersoonsgegevens.nl
                      </a>
                      ).
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Footer */}
            <div className="text-center pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Laatst bijgewerkt: januari 2026
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
